import { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';
import { StatusBadge, LoadingSpinner } from './shared';
import { handleApiError, handleApiSuccess } from '../../utils/mtls-error-handler';

interface Environment {
  id: number;
  environmentName: string;
  environmentType: string;
  apiGatewayId?: string;
  trustStoreId?: string;
  certificateCount?: number;
}

interface Assignment {
  id: number;
  certificateId: number;
  certificateName?: string;
  commonName?: string;
  deploymentStatus: string;
  createdAt: string;
}

interface Certificate {
  id: number;
  commonName: string;
  status: string;
  certificateType?: string;
}

interface Props {
  product: { id: number; name: string; organizationId?: number };
  organizationId?: number; // override org scoping if passed explicitly
  onClose: () => void;
}

export function ProductEnvironmentPanel({ product, organizationId, onClose }: Props): JSX.Element {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedEnvId, setExpandedEnvId] = useState<number | null>(null);
  const [envAssignments, setEnvAssignments] = useState<Record<number, Assignment[]>>({});
  const [loadingAssignments, setLoadingAssignments] = useState<number | null>(null);

  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTargetEnv, setAssignTargetEnv] = useState<Environment | null>(null);
  const [orgCertificates, setOrgCertificates] = useState<Certificate[]>([]);
  const [selectedCertId, setSelectedCertId] = useState<number>(0);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingEnvId, setDownloadingEnvId] = useState<number | null>(null);

  useEffect(() => {
    loadEnvironments();
  }, [product.id]);

  const loadEnvironments = async () => {
    setLoading(true);
    try {
      const res = await ApiService.listMtlsEnvironments(product.id);
      setEnvironments(res.data || []);
    } catch (e) {
      handleApiError(e, 'Environments');
    } finally {
      setLoading(false);
    }
  };

  const toggleEnvironment = async (env: Environment) => {
    if (expandedEnvId === env.id) {
      setExpandedEnvId(null);
      return;
    }
    setExpandedEnvId(env.id);
    if (!envAssignments[env.id]) {
      setLoadingAssignments(env.id);
      try {
        const res = await ApiService.listMtlsAssignments(undefined, env.id);
        let assignments: Assignment[] = res.data || [];

        // If opened from org context, filter to only certs belonging to this org
        const orgId = organizationId ?? product.organizationId;
        if (orgId && assignments.length > 0) {
          const certsRes = await ApiService.listMtlsOrganizationCertificates(orgId);
          const orgCertIds = new Set((certsRes.data || certsRes.certificates || []).map((c: any) => c.id));
          assignments = assignments.filter(a => orgCertIds.has(a.certificateId));
        }

        setEnvAssignments(prev => ({ ...prev, [env.id]: assignments }));
      } catch (e) {
        handleApiError(e, 'Assignments');
      } finally {
        setLoadingAssignments(null);
      }
    }
  };

  // Returns true if the environment is production
  const isProdEnv = (env: Environment) =>
    env.environmentType?.toLowerCase() === 'prod' ||
    env.environmentType?.toLowerCase() === 'production';

  // Filter certs to match the environment type
  const filterCertsForEnv = (certs: Certificate[], env: Environment): Certificate[] => {
    const prod = isProdEnv(env);
    return certs.filter(c => {
      const type = c.certificateType?.toLowerCase() ?? '';
      return prod ? type === 'prod' || type === 'production' : type !== 'prod' && type !== 'production';
    });
  };

  const openAssignModal = async (env: Environment) => {
    setAssignTargetEnv(env);
    setSelectedCertId(0);
    setShowAssignModal(true);
    setLoadingCerts(true);
    try {
      const orgId = organizationId ?? product.organizationId;

      // Ensure assignments are loaded so we can exclude already-assigned certs
      let currentAssignments = envAssignments[env.id];
      if (!currentAssignments) {
        const aRes = await ApiService.listMtlsAssignments(undefined, env.id);
        currentAssignments = aRes.data || [];
        setEnvAssignments(prev => ({ ...prev, [env.id]: currentAssignments }));
      }

      let allCerts: Certificate[] = [];
      if (orgId) {
        const res = await ApiService.listMtlsOrganizationCertificates(orgId);
        allCerts = res.data || res.certificates || [];
      }
      // Filter by prod/non-prod match
      const typeFiltered = filterCertsForEnv(allCerts, env);
      // Exclude certs already assigned to this env
      const alreadyAssignedIds = new Set(currentAssignments.map(a => a.certificateId));
      const filtered = typeFiltered.filter(c => !alreadyAssignedIds.has(c.id));
      setOrgCertificates(filtered);
      if (filtered.length === 1) setSelectedCertId(filtered[0].id);
    } catch (e) {
      handleApiError(e, 'Load Certificates');
    } finally {
      setLoadingCerts(false);
    }
  };

  const handleAssignConfirm = async () => {
    if (!assignTargetEnv || selectedCertId === 0) return;
    setSubmitting(true);
    try {
      await ApiService.assignMtlsCertificate(selectedCertId, assignTargetEnv.id);
      handleApiSuccess('Certificate assigned successfully', 'Assignment');
      setShowAssignModal(false);
      // Refresh this environment's assignments
      const res = await ApiService.listMtlsAssignments(undefined, assignTargetEnv.id);
      setEnvAssignments(prev => ({ ...prev, [assignTargetEnv.id]: res.data || [] }));
      loadEnvironments();
    } catch (e) {
      handleApiError(e, 'Assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async (env: Environment, assignment: Assignment) => {
    if (!confirm(`Unassign "${assignment.commonName || assignment.certificateName}" from ${env.environmentName}?`)) return;
    try {
      await ApiService.unassignMtlsCertificate(assignment.certificateId, env.id);
      handleApiSuccess('Certificate unassigned', 'Assignment');
      const res = await ApiService.listMtlsAssignments(undefined, env.id);
      setEnvAssignments(prev => ({ ...prev, [env.id]: res.data || [] }));
      loadEnvironments();
    } catch (e) {
      handleApiError(e, 'Unassign');
    }
  };

  const handleDownloadTrustStore = async (env: Environment) => {
    setDownloadingEnvId(env.id);
    try {
      const blob = await ApiService.downloadTrustStore(env.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `truststore-${env.environmentName}-${new Date().toISOString().split('T')[0]}.jks`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      handleApiSuccess('Trust store downloaded', 'Trust Store');
    } catch (e) {
      handleApiError(e, 'Trust Store Download');
    } finally {
      setDownloadingEnvId(null);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <i className="fas fa-cogs mr-2"></i>
                {product.name} — Environments & Assignments
              </h4>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body p-0">
              {loading ? (
                <div className="p-4 text-center"><LoadingSpinner text="Loading environments..." /></div>
              ) : environments.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <i className="fas fa-server fa-2x mb-2"></i>
                  <p>No environments yet. Add one to get started.</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {environments.map(env => (
                    <div key={env.id} className="list-group-item p-0">
                      {/* Environment row */}
                      <div
                        className="d-flex align-items-center px-3 py-2"
                        style={{ cursor: 'pointer', background: expandedEnvId === env.id ? '#f8f9fa' : 'white' }}
                        onClick={() => toggleEnvironment(env)}
                      >
                        <i className={`fas fa-chevron-${expandedEnvId === env.id ? 'down' : 'right'} mr-3 text-muted`}></i>
                        <div className="flex-grow-1">
                          <strong>{env.environmentName}</strong>
                          <span className="ml-2">
                            <StatusBadge status={env.environmentType || 'unknown'} type="environment" />
                          </span>
                        </div>
                        <div className="d-flex align-items-center" onClick={e => e.stopPropagation()}>
                          <span className="badge badge-info mr-3">
                            {env.certificateCount ?? 0} cert{(env.certificateCount ?? 0) !== 1 ? 's' : ''}
                          </span>
                          <button
                            className="btn btn-sm btn-success mr-1"
                            onClick={() => openAssignModal(env)}
                            title="Assign Certificate"
                          >
                            <i className="fas fa-plus mr-1"></i>Assign Cert
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleDownloadTrustStore(env)}
                            disabled={downloadingEnvId === env.id}
                            title="Download Trust Store"
                          >
                            {downloadingEnvId === env.id
                              ? <span className="spinner-border spinner-border-sm"></span>
                              : <i className="fas fa-download"></i>}
                          </button>
                        </div>
                      </div>

                      {/* Expanded assignments */}
                      {expandedEnvId === env.id && (
                        <div className="px-4 pb-3 pt-1" style={{ background: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                          {loadingAssignments === env.id ? (
                            <div className="text-center py-2"><LoadingSpinner size="sm" text="Loading assignments..." /></div>
                          ) : (envAssignments[env.id] || []).length === 0 ? (
                            <p className="text-muted mb-0 py-2">
                              <i className="fas fa-info-circle mr-1"></i>
                              No certificates assigned. Click "Assign Cert" to add one.
                            </p>
                          ) : (
                            <table className="table table-sm table-borderless mb-0">
                              <thead>
                                <tr className="text-muted small">
                                  <th>Certificate</th>
                                  <th>Deployment Status</th>
                                  <th>Assigned</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(envAssignments[env.id] || []).map(a => (
                                  <tr key={a.id}>
                                    <td><i className="fas fa-certificate mr-1 text-info"></i>{a.commonName || a.certificateName || `Cert #${a.certificateId}`}</td>
                                    <td><StatusBadge status={a.deploymentStatus} type="deployment" /></td>
                                    <td className="text-muted small">{new Date(a.createdAt).toLocaleDateString()}</td>
                                    <td>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleUnassign(env, a)}
                                        title="Unassign"
                                      >
                                        <i className="fas fa-unlink"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-default" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Assign Certificate Modal */}
      {showAssignModal && assignTargetEnv && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1070 }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-link mr-2"></i>
                    Assign Certificate to {assignTargetEnv.environmentName}
                  </h5>
                  <button type="button" className="close" onClick={() => setShowAssignModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {loadingCerts ? (
                    <LoadingSpinner text="Loading certificates..." />
                  ) : orgCertificates.length === 0 ? (
                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      No {isProdEnv(assignTargetEnv) ? 'production' : 'non-production'} certificates available for this organisation.
                      Upload a {isProdEnv(assignTargetEnv) ? 'prod' : 'non-prod'} certificate first.
                    </div>
                  ) : (
                    <>
                      <div className={`alert ${isProdEnv(assignTargetEnv) ? 'alert-danger' : 'alert-info'} py-2`}>
                        <i className={`fas ${isProdEnv(assignTargetEnv) ? 'fa-shield-alt' : 'fa-info-circle'} mr-2`}></i>
                        <strong>{isProdEnv(assignTargetEnv) ? 'Production' : 'Non-production'} environment</strong>
                        {' '}— showing only {isProdEnv(assignTargetEnv) ? 'production' : 'non-production'} certificates.
                      </div>
                      <div className="form-group mb-0">
                        <label>Select Certificate</label>
                        <select
                          className="form-control"
                          value={selectedCertId}
                          onChange={e => setSelectedCertId(parseInt(e.target.value))}
                        >
                          {orgCertificates.length > 1 && <option value={0}>-- Select a certificate --</option>}
                          {orgCertificates.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.commonName} — {c.status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer justify-content-between">
                  <button className="btn btn-default" onClick={() => setShowAssignModal(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAssignConfirm}
                    disabled={submitting || selectedCertId === 0}
                  >
                    {submitting && <span className="spinner-border spinner-border-sm mr-2"></span>}
                    Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
